import React, { useEffect, useState } from 'react';
import {
  createInstance,
  OptimizelyProvider,
  ReactSDKClient,
} from '@optimizely/react-sdk';
import { uuid } from 'uuidv4';
import { AnchorAPI } from '../../modules/AnchorAPI';
import * as serverRenderingUtils from '../../../helpers/serverRenderingUtils';

import { useCurrentUserCtx } from '../CurrentUser';
import { localStorage } from '../../modules/Browser/localStorage';

declare global {
  interface Window {
    __OPTIMIZELY_API_KEY__: string;
    optimizely: ReactSDKClient;
  }
}

declare type UserInfo = {
  id: string;
  attributes?: { [name: string]: any };
};

const optimizelySdk = createInstance({
  sdkKey: window.__OPTIMIZELY_API_KEY__,
  // @ts-ignore - Optimizely types are wrong; set logLevel to `null` to stop logging: https://docs.developers.optimizely.com/full-stack/docs/customize-logger-react
  // eslint-disable-next-line no-undef
  logLevel: BUILD_ENVIRONMENT.startsWith('development') ? 'debug' : null,
  datafileOptions: {
    autoUpdate: true,
  },
});

if (window) {
  window.optimizely = optimizelySdk;
}

const OPTIMIZELY_INDEXEDDB_KEY = 'optimizely_partner_id';

export const ExperimentationProvider = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const {
    state: { userId },
  } = useCurrentUserCtx();

  const [partnerId, setPartnerId] = useState<UserInfo | undefined>();

  const setLocalPartnerId = () => {
    localStorage.getItem(OPTIMIZELY_INDEXEDDB_KEY).then(optimizelyPartnerId => {
      if (optimizelyPartnerId) {
        setPartnerId({
          id: optimizelyPartnerId,
          attributes: { userId: optimizelyPartnerId },
        });
      } else {
        const newPartnerId = uuid();
        localStorage.setItem(OPTIMIZELY_INDEXEDDB_KEY, newPartnerId);
        setPartnerId({
          id: newPartnerId,
          attributes: { userId: newPartnerId },
        });
      }
    });
  };

  useEffect(() => {
    if (userId) {
      AnchorAPI.fetchPartnerIds(userId)
        .then(resp => {
          setPartnerId({
            id: resp.optimizely,
            attributes: { userId: resp.optimizely },
          });
        })
        .catch(() => {
          setLocalPartnerId();
        });
    } else {
      setLocalPartnerId();
    }
  }, [userId]);

  if (serverRenderingUtils.windowUndefined()) return children;

  return (
    <OptimizelyProvider
      optimizely={optimizelySdk}
      timeout={5000}
      user={partnerId}
    >
      {children}
    </OptimizelyProvider>
  );
};
